import { StepperSelectionEvent } from '@angular/cdk/stepper';
import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	Renderer2,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { Map } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	filter,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';

import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { HdIcons } from '../../../ui/app-hd.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import { ColumnConfigItemEntity } from '../../domain/column-config-item.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../../domain/diagnostic-errors.entity';
import { InternalMetadataEntity } from '../../domain/internal-metadata.entity';
import { ProfileItemEntity } from '../../domain/profile-item.entity';
import { PublicMetadataEntity } from '../../domain/public-metadata.entity';
import { SubscriptionProvider } from '../../providers/subscription.provider';
import {
	closeNewSubscriptionModal,
	getDatablockColumns,
	getSourceColumns,
	initSubscriptionStepper,
	refreshSourceColumns,
	refreshSubscriptionDatablockList,
	SubscriptionCreatorSelector,
} from '../../store';
import { ColumnsConfigComponent } from '../columns-config/columns-config.component';
import { InternalMetadataConfigComponent } from '../internal-metadata-config/internal-metadata-config.component';
import { ProfileConfigComponent } from '../profile-config/profile-config.component';
import { PublicMetadataConfigComponent } from '../public-metadata-config/public-metadata-config.component';
import { DatablockListProvider } from './datablock-list.provider';
import { SubscriptionDatablockItemEntity } from './subscription-datablock-item.entity';
import { SubscriptionDatablockListEntity } from './subscription-datablock-list.entity';
import { SubscriptionStepperViewProvider } from './subscription-stepper-view.provider';

enum SubscriptionConfigurationStep {
	Datablock = 0,
	Metadata = 1,
	ColumnsSelection = 2,
	ColumnsProperties = 3,
	Profile = 4,
}

type SubscriptionOverlayDatablock = {
	datablockId: number | undefined;
};

type DatablockType = {
	id: number;
	label: string;
};

@Component({
	selector: 'app-subscription-configuration-stepper',
	templateUrl: './subscription-configuration-stepper.component.html',
	styleUrls: ['./subscription-configuration-stepper.component.scss'],
	providers: [
		SubscriptionStepperViewProvider,
		DatablockListProvider,
		SubscriptionProvider,
	],
})
export class SubscriptionConfigurationStepperComponent<
		T extends { datablockId: number; datablockLabel: string }
	>
	extends DcBaseComponent
	implements OnInit, AfterViewInit
{
	protected readonly SubscriptionConfigurationStep =
		SubscriptionConfigurationStep;
	protected readonly DcLoaders = DcLoaders;
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly HdIcons = HdIcons;
	protected readonly SubscriptionErrorKey = SubscriptionErrorKey;

	@ViewChild(MatStepper)
	public stepper: MatStepper | null = null;

	public vo$: Observable<{
		currentStep: SubscriptionConfigurationStep;
		datablockList: SubscriptionDatablockListEntity;
		isDatablockSelectionEnabled: boolean;
		internalMetadata: InternalMetadataEntity;
		publicMetadata: PublicMetadataEntity;
		openedOverlayDatablock: SubscriptionOverlayDatablock;
		stepValidity: boolean;
		datablockColumns: Map<string, ColumnConfigItemEntity>;
		subscriptionColumns: Map<string, ColumnConfigItemEntity>;
		columnsPanelActions: Array<PanelAction>;
		error: SubscriptionDiagnosticError | undefined;
	}>;

	public profileConfig = ProfileItemEntity.build();

	@ViewChild(DxDataGridComponent)
	private readonly datablockGrid: DxDataGridComponent | null = null;

	@ViewChild(InternalMetadataConfigComponent)
	private readonly subscriptionInternalMetadataCmp: InternalMetadataConfigComponent | null =
		null;

	@ViewChild(PublicMetadataConfigComponent)
	private readonly subscriptionPublicMetadataCmp: PublicMetadataConfigComponent | null =
		null;

	@ViewChild('columnsSelection')
	private readonly columnsSelectionCmp: ColumnsConfigComponent | null = null;

	@ViewChild('columnsProperties')
	private readonly columnsPropertiesCmp: ColumnsConfigComponent | null = null;

	@ViewChild(ProfileConfigComponent)
	private readonly subscriptionProfileModelCmp: ProfileConfigComponent | null =
		null;

	private readonly openedOverlayDatablock$ =
		new BehaviorSubject<SubscriptionOverlayDatablock>({
			datablockId: undefined,
		});

	private readonly currentStep$ =
		new BehaviorSubject<SubscriptionConfigurationStep>(
			SubscriptionConfigurationStep.Datablock
		);

	private readonly previousStep$ =
		new ReplaySubject<SubscriptionConfigurationStep>(1);

	private readonly stepValidity$ = new BehaviorSubject<boolean>(false);

	private readonly selectedDatablock$ =
		new BehaviorSubject<DatablockType | null>(null);

	private readonly isDatablockSelectionEnabled$ = new BehaviorSubject<boolean>(
		true
	);

	private readonly internalMetadata$ =
		new BehaviorSubject<InternalMetadataEntity>(InternalMetadataEntity.build());

	private readonly publicMetadata$ = new BehaviorSubject<PublicMetadataEntity>(
		PublicMetadataEntity.build()
	);

	private isNewDatablockSelection = false;

	private readonly datablockColumns$ = new BehaviorSubject<
		Map<string, ColumnConfigItemEntity>
	>(Map());

	private readonly columnActions$ = new BehaviorSubject<Array<PanelAction>>([]);

	public constructor(
		private readonly store: Store,
		private readonly selector: SubscriptionCreatorSelector,
		private readonly modalService: AppModalService,
		private readonly renderer: Renderer2,
		private readonly elemRef: ElementRef,
		private readonly subscriptionStepperViewProvider: SubscriptionStepperViewProvider,
		private readonly datablockListProvider: DatablockListProvider,
		private readonly subscriptionProvider: SubscriptionProvider,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'subscription-configuration-stepper';

		this.subscriptionProvider.configure({
			[SubscriptionConfigurationStep.Datablock]: [
				SubscriptionErrorKey.NoSelectDatablock,
			],
			[SubscriptionConfigurationStep.Metadata]: [
				SubscriptionErrorKey.InvalidInternalMetadata,
				SubscriptionErrorKey.InvalidPublicMetadata,
			],
			[SubscriptionConfigurationStep.ColumnsSelection]: [
				SubscriptionErrorKey.NoSelectColumns,
			],
			[SubscriptionConfigurationStep.ColumnsProperties]: [
				SubscriptionErrorKey.InvalidSubscriptionColumns,
			],
			[SubscriptionConfigurationStep.Profile]: [
				SubscriptionErrorKey.ProfileMissingFields,
				SubscriptionErrorKey.NoSelectColumnModel,
				SubscriptionErrorKey.NoSelectFilterModel,
			],
		});

		const datablockList$ = this.selector
			.getShareableDatablocks$()
			.pipe(map((items) => this.datablockListProvider.setListElements(items)));

		const errors$ = combineLatest([
			this.subscriptionProvider.errors$,
			this.currentStep$,
		]).pipe(
			map(([, currentStep]) => ({
				currentStep,
				stepErrors: this.subscriptionProvider.getErrorsForStep(currentStep),
			})),
			tap(({ currentStep, stepErrors }) => {
				const currentErrors = stepErrors.filter(
					(error) => error.type === 'critic'
				);
				this.stepValidity$.next(currentErrors.size === 0);

				if (
					currentStep !== SubscriptionConfigurationStep.ColumnsSelection &&
					currentStep !== SubscriptionConfigurationStep.ColumnsProperties
				) {
					return;
				}

				return this.bootstrapIcons(currentErrors);
			}),
			map(({ stepErrors }) => stepErrors)
		);

		this.vo$ = combineAll({
			currentStep: this.currentStep$,
			datablockList: datablockList$,
			isDatablockSelectionEnabled: this.isDatablockSelectionEnabled$,
			internalMetadata: this.internalMetadata$,
			publicMetadata: this.publicMetadata$,
			openedOverlayDatablock: this.openedOverlayDatablock$,
			stepValidity: this.stepValidity$,
			datablockColumns: this.datablockColumns$,
			subscriptionColumns: this.datablockColumns$.pipe(
				map((columns) => columns.filter((item) => item.isSelectedInSource))
			),
			columnsPanelActions: this.columnActions$,
			error: errors$.pipe(map((errors) => errors.valueSeq().first())),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		if (this.modalData.datablockId && this.modalData.datablockLabel) {
			this.selectedDatablock$.next({
				id: this.modalData.datablockId,
				label: this.modalData.datablockLabel,
			});
			this.isDatablockSelectionEnabled$.next(false);
			this.isNewDatablockSelection = true;
			this.currentStep$.next(SubscriptionConfigurationStep.Metadata);
		}

		this.handleSubscription();

		this.store.dispatch(initSubscriptionStepper());
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		setTimeout(
			() =>
				this.subscriptionStepperViewProvider.adjustHeights(
					this.elemRef,
					this.renderer
				),
			750
		);
	}

	public selectDatablock(event: SelectionChangedEvent): void {
		const datablock = event
			.selectedRowsData[0] as SubscriptionDatablockItemEntity;
		if (datablock) {
			this.selectedDatablock$.next({
				id: datablock.id,
				label: datablock.label,
			});
			this.isNewDatablockSelection = true;
		}
	}

	public search(searchTerm: string): void {
		if (this.datablockGrid === null) {
			return;
		}
		this.datablockGrid.instance.searchByText(searchTerm.trim());
	}

	public async onStepReady(): Promise<void> {
		const currentStep = await firstValueFrom(this.currentStep$);
		const previousStep = await firstValueFrom(this.previousStep$);

		if (previousStep === SubscriptionConfigurationStep.ColumnsSelection) {
			if (!this.columnsSelectionCmp) {
				return;
			}

			const elements = await this.columnsSelectionCmp.getUpdatedColumns();

			this.datablockColumns$.next(elements);
		}

		if (previousStep === SubscriptionConfigurationStep.ColumnsProperties) {
			if (!this.columnsPropertiesCmp || !this.columnsSelectionCmp) {
				return;
			}

			let datablockColumns = await firstValueFrom(this.datablockColumns$);

			const columnsConfig = await this.columnsPropertiesCmp.getUpdatedColumns();

			columnsConfig.forEach((col) => {
				datablockColumns = datablockColumns.set(col.id, col);
			});

			this.datablockColumns$.next(datablockColumns);
		}

		if (currentStep === SubscriptionConfigurationStep.ColumnsSelection) {
			const selectedDatablock = await firstValueFrom(this.selectedDatablock$);
			if (!selectedDatablock || !this.isNewDatablockSelection) {
				return;
			}

			this.store.dispatch(
				getSourceColumns({
					datablockId: selectedDatablock.id,
				})
			);
			this.isNewDatablockSelection = false;
		}
	}

	public changeCurrentStep(event: StepperSelectionEvent): void {
		const previousStep =
			event.previouslySelectedIndex as SubscriptionConfigurationStep;
		const currentStep = event.selectedIndex as SubscriptionConfigurationStep;
		this.stepValidity$.next(false);
		this.currentStep$.next(currentStep);
		this.previousStep$.next(previousStep);
	}

	public backStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.previous();
	}

	public nextStep(): void {
		if (this.stepper === null) {
			return;
		}

		this.stepper.next();
	}

	public async openColumnsPopover(
		event: Event,
		datablockId: number
	): Promise<void> {
		event.stopPropagation();

		const previous = await firstValueFrom(this.openedOverlayDatablock$);
		if (previous.datablockId === datablockId) {
			this.openedOverlayDatablock$.next({
				...previous,
				datablockId: undefined,
			});
			return;
		}

		this.openedOverlayDatablock$.next({
			...previous,
			datablockId,
		});

		this.store.dispatch(
			getDatablockColumns({
				datablockId,
			})
		);
	}

	public closeColumnsPopover(): void {
		this.openedOverlayDatablock$.next({
			datablockId: undefined,
		});
	}

	public refreshDatablocks(): void {
		this.store.dispatch(refreshSubscriptionDatablockList());
	}

	public async refreshColumns(): Promise<void> {
		const selectedDatablock = await firstValueFrom(this.selectedDatablock$);

		if (!selectedDatablock) {
			return;
		}

		this.store.dispatch(
			refreshSourceColumns({
				datablockId: selectedDatablock.id,
			})
		);
	}

	public async saveNewSubscription(): Promise<void> {
		const selectedDatablock = await firstValueFrom(this.selectedDatablock$);

		if (!selectedDatablock) {
			return;
		}

		if (
			!this.subscriptionProfileModelCmp ||
			!this.subscriptionInternalMetadataCmp ||
			!this.subscriptionPublicMetadataCmp ||
			!this.columnsPropertiesCmp
		) {
			return;
		}

		const internalMetadata =
			await this.subscriptionInternalMetadataCmp.extractFormData();
		const publicMetadata =
			await this.subscriptionPublicMetadataCmp.extractFormData();
		const subscriptionColumns =
			await this.columnsPropertiesCmp.getUpdatedColumns();

		const profileConfig = this.subscriptionProfileModelCmp.getProfileConfig();
		if (!profileConfig) {
			return;
		}

		this.modalService.closeActiveModal({
			datablockId: selectedDatablock.id,
			internalMetadata,
			publicMetadata,
			columns: subscriptionColumns,
			profile: profileConfig.metadata,
			columnModel: profileConfig.columnModels[0],
			filterModels: profileConfig.filterModels,
		});
	}

	public close(): void {
		this.store.dispatch(
			closeNewSubscriptionModal({
				datablockId: this.modalData.datablockId,
			})
		);
	}

	private handleSubscription(): void {
		this.currentStep$
			.pipe(
				takeUntil(this.onDestroy$),
				filter(
					(step) =>
						step === SubscriptionConfigurationStep.ColumnsSelection ||
						step === SubscriptionConfigurationStep.ColumnsProperties
				),
				tap(() => {
					setTimeout(
						() =>
							this.subscriptionStepperViewProvider.adjustHeights(
								this.elemRef,
								this.renderer
							),
						750
					);
				})
			)
			.subscribe();

		this.selector
			.getAvailableColumns$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((columns) => {
					this.datablockColumns$.next(columns);
				})
			)
			.subscribe();

		this.selectedDatablock$
			.pipe(
				takeUntil(this.onDestroy$),
				tap((selectedDatablock) => {
					if (selectedDatablock === null) {
						this.subscriptionProvider.addCriticalError(
							$localize`:i18n=@@subscription.diagnosticError.noSelectDatablock:`,
							SubscriptionErrorKey.NoSelectDatablock
						);
					} else {
						this.subscriptionProvider.removeError(
							SubscriptionErrorKey.NoSelectDatablock
						);
					}
				}),
				filter((datablock): datablock is DatablockType => datablock !== null),
				tap((selectedDatablock) => {
					this.internalMetadata$.next(
						InternalMetadataEntity.build({
							label: `${$localize`:i18n=@@general.element.subscription:`} - ${
								selectedDatablock.label
							}`,
						})
					);
					this.publicMetadata$.next(
						PublicMetadataEntity.build({
							title: `${$localize`:i18n=@@general.element.subscription:`} - ${
								selectedDatablock.label
							}`,
						})
					);
				})
			)
			.subscribe();
	}

	private async bootstrapIcons(
		errors: Map<SubscriptionErrorKey, SubscriptionDiagnosticError>
	): Promise<void> {
		const actions = [ActionIcons.FilterWarning];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		if (errors.size === 0) {
			this.columnActions$.next([]);
			if (this.columnsPropertiesCmp) {
				this.columnsPropertiesCmp.clearFilter();
			}
			return;
		}

		if (errors.has(SubscriptionErrorKey.NoSelectColumns)) {
			this.columnActions$.next([]);
			return;
		}

		this.columnActions$.next([
			{
				order: 0,
				command: (): void => {
					if (this.columnsPropertiesCmp) {
						this.columnsPropertiesCmp.filterColumnConfigErrors();
					}
				},
				iconName: actionMap.get(ActionIcons.FilterWarning)?.iconName,
				tooltip: $localize`:@@subscription.columns.config.action.filterErrors:`,
				description: $localize`:@@subscription.columns.config.action.filterErrors:`,
				typed: '',
			},
		]);
	}
}
